import React, { useCallback, useState, useRef, useEffect } from 'react';

import { Icon } from 'design-system-web';
import cx from 'classnames';
import { createEditor, Editor, Text, Transforms, Range, Element } from 'slate';
import { withHistory } from 'slate-history';
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react';

import { FeatureFlag } from 'constants-flags';

import useFlag from 'lane-shared/hooks/useFlag';
import {
  RichTextAlignEnum,
  RichTextBlockEnum,
  RichTextMarksEnum,
  RichTextToolsEnum,
} from 'lane-shared/properties/baseTypes/RichText';

import LinkEditor from './LinkEditor';
import defaultValue from './RichText.default.json';
import { countChars, withCharLimit } from './withCharLimit';
import { withImeDedup, ImeDedupEditor } from './withImeCompositionDedup';
import withLinks, { unwrapLink, wrapLink } from './withLinks';

import styles from './styles.scss';

const NoFormat = 'remove-format';

RichText.defaultValue = defaultValue;

export default function RichText({
  className,
  inputClassName,
  toolBarClassName,
  style,
  value = null,
  toolbar = RichText.toolbar,
  onChange = () => null,
  maxCharacters = null,
  testId,
  readOnly = false,
}: any) {
  const isFixRichTextEditorInSafariEnabled = useFlag(
    FeatureFlag.FixRichTextEditorInSafari,
    false
  );
  const isChineseCharFixEnabled = useFlag(
    FeatureFlag.ChineseCharactersDuplicationFix,
    false
  );

  // Refs so the editor closure always reads the latest values without needing
  // to re-create the editor when LaunchDarkly resolves asynchronously.
  const isChineseCharFixEnabledRef = useRef(!!isChineseCharFixEnabled);
  const isComposingRef = useRef(false);
  const lastCompositionEndRef = useRef(0);

  useEffect(() => {
    isChineseCharFixEnabledRef.current = !!isChineseCharFixEnabled;
  }, [isChineseCharFixEnabled]);

  const [count, setCount] = useState(0);
  const [editor] = useState<ImeDedupEditor<ReturnType<typeof withCharLimit>>>(
    () => {
      const base = withCharLimit(
        withLinks(withHistory(withReact(createEditor()))),
        maxCharacters
      );

      // Slate 0.59 on Windows Chrome fires insertText twice for a single IME
      // commit (once from beforeinput, once from compositionend). Dedup is
      // gated on composition state so normal typing and OS key-repeat are
      // never affected — only double-fires inside/just-after a composition
      // session are dropped.
      return withImeDedup(base, {
        isEnabled: isChineseCharFixEnabledRef,
        isComposing: isComposingRef,
        lastCompositionEnd: lastCompositionEndRef,
      });
    }
  );

  const lastSelectionRef = useRef<Range | null>(null);
  // just forces the UI to update when the user selects a node.
  const [, setLastRender] = useState<number>(Date.now());

  useEffect(() => {
    setCount(countChars(editor));
  }, [value]);

  const renderCharLimitCounter = () => {
    return `${count}/${maxCharacters}`;
  };

  const hasMark = useCallback((mark: string): boolean => {
    const [match] = Editor.nodes(editor, {
      match: n => n[mark] === true,
      universal: true,
    });

    return !!match;
  }, []);

  const hasBlock = useCallback((type: string): boolean => {
    const [match] = Editor.nodes(editor, {
      match: n => n.type === type,
    });

    return !!match;
  }, []);

  const hasAlign = useCallback((type: string): boolean => {
    const [match] = Editor.nodes(editor, {
      match: n => n?.align === type,
    });

    return !!match;
  }, []);

  function onClickMark(e: any, type: any) {
    e.preventDefault();

    const isActive = hasMark(type);

    Transforms.setNodes(
      editor,
      { [type]: isActive ? null : true },
      { match: n => Text.isText(n), split: true }
    );
  }

  function updateLink(url: URL, element: Element) {
    Transforms.setNodes(
      editor,
      { url },
      {
        at: ReactEditor.findPath(editor, element),
        match: n => n.type === RichTextBlockEnum.Link,
      }
    );
  }

  function removeLink() {
    editor.selection = lastSelectionRef.current;
    unwrapLink(editor);
  }

  function editableClicked() {
    // we will need this if the user moves off focus on the edit component
    lastSelectionRef.current = editor.selection;
    setLastRender(Date.now());
  }

  // Tracks whether a toolbar button press is still valid (i.e. the pointer
  // hasn't been dragged off the button before release). This recreates the
  // native click-abort behaviour that is lost when we move from onClick to
  // onMouseDown to prevent Safari from stealing focus.
  const pendingToolbarActionRef = useRef(false);

  function handleToolbarMouseDown(
    e: React.MouseEvent<HTMLButtonElement>,
    action: (e: React.MouseEvent<HTMLButtonElement>) => void
  ) {
    e.preventDefault();
    pendingToolbarActionRef.current = true;

    const button = e.currentTarget;

    function onMouseUp(upEvent: MouseEvent) {
      if (pendingToolbarActionRef.current) {
        action(upEvent as unknown as React.MouseEvent<HTMLButtonElement>);
      }

      pendingToolbarActionRef.current = false;
      button.removeEventListener('mouseup', onMouseUp);
      button.removeEventListener('mouseleave', onMouseLeave);
    }

    function onMouseLeave() {
      // Pointer dragged off — abort the pending action.
      pendingToolbarActionRef.current = false;
      button.removeEventListener('mouseup', onMouseUp);
      button.removeEventListener('mouseleave', onMouseLeave);
    }

    button.addEventListener('mouseup', onMouseUp);
    button.addEventListener('mouseleave', onMouseLeave);
  }

  function handleToolbarKeyboardClick(
    e: React.MouseEvent<HTMLButtonElement>,
    action: (e: React.MouseEvent<HTMLButtonElement>) => void
  ) {
    // e.detail === 0 means the click was keyboard-triggered (Space/Enter)
    // instead of mouse click
    if (e.detail === 0) {
      action(e);
    }
  }

  function onClickBlock(e: any, type: any) {
    e.preventDefault();

    const isActive = hasBlock(type) || hasAlign(type);

    if (type === RichTextBlockEnum.Link) {
      if (isActive) {
        unwrapLink(editor);

        return;
      }

      wrapLink(editor, 'http://example.com');

      return;
    }

    if (Object.values(RichTextAlignEnum).includes(type)) {
      Transforms.setNodes(
        editor,
        { align: isActive ? null : type },
        { match: n => Editor.isBlock(editor, n) }
      );

      return;
    }

    Transforms.setNodes(
      editor,
      { type: isActive ? null : type },
      { match: n => Editor.isBlock(editor, n) }
    );
  }

  function onClickClear(e: any) {
    e.preventDefault();
    Object.values(RichTextMarksEnum).forEach(type =>
      Transforms.setNodes(
        editor,
        { [type]: false },
        { match: n => Text.isText(n) }
      )
    );
    ['align', 'type'].forEach(key =>
      Transforms.setNodes(
        editor,
        { [key]: null },
        { match: n => Editor.isBlock(editor, n) }
      )
    );
  }

  function renderClearButton({ type, icon }: any) {
    return (
      <button
        key={type}
        className={
          readOnly ? styles.toolbarButtonDisabled : styles.toolbarButton
        }
        disabled={readOnly}
        {...(isFixRichTextEditorInSafariEnabled
          ? {
              onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) =>
                handleToolbarMouseDown(e, onClickClear),
              onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
                handleToolbarKeyboardClick(e, onClickClear),
            }
          : {
              onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
                onClickClear(e),
            })}
      >
        <Icon name={icon} />
      </button>
    );
  }

  function renderMarkButton({ type, icon }: any) {
    const isActive = hasMark(type);

    return (
      <button
        key={type}
        data-is-active={isActive}
        className={
          readOnly ? styles.toolbarButtonDisabled : styles.toolbarButton
        }
        disabled={readOnly}
        {...(isFixRichTextEditorInSafariEnabled
          ? {
              onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) =>
                handleToolbarMouseDown(e, ev => onClickMark(ev, type)),
              onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
                handleToolbarKeyboardClick(e, ev => onClickMark(ev, type)),
            }
          : {
              onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
                onClickMark(e, type),
            })}
      >
        <Icon name={icon} />
      </button>
    );
  }

  // Moves the selector back to the start of the node on leaving focus. This is important as a race condition occurs as
  // we swap between two editors. If one is has more nodes than the other, this result in selector having an invalid position
  // resulting in a crash
  function moveSelectionToStart() {
    lastSelectionRef.current = editor.selection;
    Transforms.select(editor, Editor.start(editor, []));
  }

  function restoreSelection() {
    editor.selection = lastSelectionRef.current;
    lastSelectionRef.current = editor.selection;
  }

  function renderBlockButton({ type, icon }: any) {
    const isActive = hasBlock(type) || hasAlign(type);

    return (
      <button
        key={type}
        className={
          readOnly ? styles.toolbarButtonDisabled : styles.toolbarButton
        }
        disabled={readOnly}
        data-is-active={isActive}
        {...(isFixRichTextEditorInSafariEnabled
          ? {
              onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) =>
                handleToolbarMouseDown(e, ev => onClickBlock(ev, type)),
              onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
                handleToolbarKeyboardClick(e, ev => onClickBlock(ev, type)),
            }
          : {
              onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
                onClickBlock(e, type),
            })}
      >
        <Icon name={icon} />
      </button>
    );
  }

  const renderLeaf = useCallback((props: RenderLeafProps) => {
    return (
      <span
        {...props.attributes}
        style={{
          fontWeight: props.leaf.bold ? 'bold' : 'normal',
          textDecoration: props.leaf.underlined ? 'underline' : 'none',
          fontStyle: props.leaf.italic ? 'italic' : 'normal',
        }}
      >
        {props.children}
      </span>
    );
  }, []);

  const renderElement = useCallback(function (props: RenderElementProps) {
    const { attributes, children, element } = props;

    const alignStyle = { textAlign: element?.align || RichTextAlignEnum.left };

    switch (props.element.type) {
      case RichTextBlockEnum.BlockQuote:
        return (
          // @ts-expect-error
          <blockquote style={alignStyle} {...attributes}>
            {children}
          </blockquote>
        );
      case RichTextBlockEnum.BulletedList:
        return <ul {...attributes}>{children}</ul>;
      case RichTextBlockEnum.H1:
        return (
          // @ts-expect-error
          <h1 style={{ ...alignStyle, fontSize: '2em' }} {...attributes}>
            {children}
          </h1>
        );
      case RichTextBlockEnum.H2:
        return (
          // @ts-expect-error
          <h2 style={alignStyle} {...attributes}>
            {children}
          </h2>
        );
      case RichTextBlockEnum.H3:
        return (
          // @ts-expect-error
          <h3 style={{ ...alignStyle, fontSize: '1.25em' }} {...attributes}>
            {children}
          </h3>
        );
      case RichTextBlockEnum.H4:
        return (
          // @ts-expect-error
          <h4 style={{ ...alignStyle, fontSize: '1.1em' }} {...attributes}>
            {children}
          </h4>
        );
      case RichTextBlockEnum.H5:
        return (
          // @ts-expect-error
          <h5 style={alignStyle} {...attributes}>
            {children}
          </h5>
        );
      case RichTextBlockEnum.H6:
        return (
          // @ts-expect-error
          <h6 style={alignStyle} {...attributes}>
            {children}
          </h6>
        );
      case RichTextBlockEnum.ListItem:
        return <li {...attributes}>{children}</li>;
      case RichTextBlockEnum.NumberedList:
        return <ol {...attributes}>{children}</ol>;
      case RichTextBlockEnum.Code:
        return (
          // @ts-expect-error
          <code style={alignStyle} {...attributes}>
            {children}
          </code>
        );
      case RichTextBlockEnum.Link:
        return (
          <LinkEditor
            {...attributes}
            element={element}
            onClear={removeLink}
            onChange={updateLink}
          >
            {children}
          </LinkEditor>
        );
      case RichTextBlockEnum.Paragraph:
      default:
        return (
          // @ts-expect-error
          <p style={{ ...alignStyle, margin: 0 }} {...attributes}>
            {children}
          </p>
        );
    }
  }, []);

  return (
    <div
      className={cx(styles.RichText, className)}
      style={style}
      data-test={testId}
    >
      <div className={inputClassName}>
        <div
          onBlur={moveSelectionToStart}
          onFocus={restoreSelection}
          className={cx(
            readOnly ? styles.ToolbarDisabled : styles.Toolbar,
            toolBarClassName
          )}
        >
          {toolbar.map(({ type, icon, toolType }: any) => {
            switch (toolType) {
              case RichTextToolsEnum.Mark:
                return renderMarkButton({ type, icon });
              case RichTextToolsEnum.Block:
                return renderBlockButton({ type, icon });
              case RichTextToolsEnum.Clear:
                return renderClearButton({ type, icon });
              default:
                return null;
            }
          })}
        </div>

        <Slate
          editor={editor}
          value={
            value || [
              {
                type: 'paragraph',
                children: [
                  {
                    text: '',
                  },
                ],
              },
            ]
          }
          onChange={newValue => onChange(newValue)}
        >
          <Editable
            data-test="bodyText"
            className={styles.editArea}
            renderLeaf={renderLeaf}
            renderElement={renderElement}
            onBlur={moveSelectionToStart}
            onClick={_e => editableClicked()}
            onCompositionStart={() => {
              isComposingRef.current = true;
              editor.resetImeDedup();
            }}
            onCompositionEnd={() => {
              isComposingRef.current = false;
              lastCompositionEndRef.current = Date.now();
            }}
            readOnly={readOnly}
          />
        </Slate>
      </div>
      {maxCharacters && (
        <span className={styles.charCounter}>{renderCharLimitCounter()}</span>
      )}
    </div>
  );
}

RichText.toolbar = [
  {
    type: RichTextMarksEnum.Bold,
    icon: 'bold',
    toolType: RichTextToolsEnum.Mark,
  },
  {
    type: RichTextMarksEnum.Italic,
    icon: 'italic',
    toolType: RichTextToolsEnum.Mark,
  },
  {
    type: RichTextMarksEnum.Underlined,
    icon: 'underline',
    toolType: RichTextToolsEnum.Mark,
  },
  {
    type: RichTextBlockEnum.Link,
    icon: 'link',
    toolType: RichTextToolsEnum.Block,
  },
  { type: NoFormat, icon: 'remove-format', toolType: RichTextToolsEnum.Clear },
  { type: RichTextBlockEnum.H1, icon: 'h1', toolType: RichTextToolsEnum.Block },
  { type: RichTextBlockEnum.H2, icon: 'h2', toolType: RichTextToolsEnum.Block },
  { type: RichTextBlockEnum.H3, icon: 'h3', toolType: RichTextToolsEnum.Block },
  { type: RichTextBlockEnum.H4, icon: 'h4', toolType: RichTextToolsEnum.Block },
  {
    type: RichTextAlignEnum.left,
    icon: 'align-left',
    toolType: RichTextToolsEnum.Block,
  },
  {
    type: RichTextAlignEnum.center,
    icon: 'align-center',
    toolType: RichTextToolsEnum.Block,
  },
  {
    type: RichTextAlignEnum.right,
    icon: 'align-right',
    toolType: RichTextToolsEnum.Block,
  },
];
